import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { injectQuery, injectQueryClient } from '@ngneat/query'
import { Query, QueryStatus } from '@tanstack/query-core'
import { catchError, combineLatest, distinctUntilChanged, forkJoin, map, Observable, of, shareReplay } from 'rxjs'
import { worstStatePerEnvironment } from './environment-merge'
import { isSelfEnvironment, SUPPORT_ENVIRONMENTS, SupportEnvironment } from './support-environment'

export type ConnectionState = 'checking' | 'connected' | 'signInRequired' | 'notPermitted' | 'unreachable'

export type EnvironmentResult<T> = {
  environment: SupportEnvironment
  state: ConnectionState
  data: T | null
  checkedAt: Date | null
}

export type FanOutParams = Record<string, string | number | boolean | readonly number[] | readonly string[]>

export type FanOutRequest = {
  path: string
  // A function when the request carries row ids: those are per-database, so each environment has to
  // be asked about its own. The queryKey still has to distinguish the whole selection.
  params?: FanOutParams | ((environment: SupportEnvironment) => FanOutParams)
  queryKey: readonly unknown[]
  refetchInterval?: number
  /**
   * Environments this request has nothing to ask about are left out rather than sent a selection that
   * names none of their rows. They would answer with nothing, so the saving is the round trip — one
   * per environment, on every change to a filter that usually names a single one of them.
   */
  appliesTo?: (environment: SupportEnvironment) => boolean
}

/**
 * Atlas does almost nothing but read, so a write says more about itself than a read does: it is one
 * shot with no cache behind it, and the interesting answer is per environment rather than merged.
 * "Skipped" is deliberately not a failure — an environment the caller decided this write does not
 * apply to has not gone wrong.
 */
export type EnvironmentWriteState = 'created' | 'skipped' | 'rejected' | 'signInRequired' | 'notPermitted' | 'unreachable'

export type EnvironmentWriteResult<T> = {
  environment: SupportEnvironment
  state: EnvironmentWriteState
  data: T | null
  // What the environment said when it refused, when it said anything worth repeating.
  reason: string | null
}

export type WriteFanOutRequest<B> = {
  path: string
  // Per environment, because a body carrying row ids means something different in each one. Null for
  // an environment this write does not apply to.
  body: (environment: SupportEnvironment) => B | null
}

function messageFromError(error: HttpErrorResponse): string | null {
  const body: unknown = error.error
  const message = typeof body === 'object' && body !== null ? (body as { message?: unknown }).message : body
  if (Array.isArray(message)) {
    return message.join('; ')
  }
  return typeof message === 'string' && message.length > 0 ? message : null
}

function writeStateFromError(error: unknown): { state: EnvironmentWriteState; reason: string | null } {
  if (!(error instanceof HttpErrorResponse)) {
    return { state: 'unreachable', reason: null }
  }
  if (error.status === 401) {
    return { state: 'signInRequired', reason: null }
  }
  if (error.status === 403) {
    return { state: 'notPermitted', reason: null }
  }
  // Anything else in the 4xx range is this environment having read the request and turned it down,
  // which is worth repeating verbatim: it is nearly always a value that means nothing here.
  if (error.status >= 400 && error.status < 500) {
    return { state: 'rejected', reason: messageFromError(error) }
  }
  return { state: 'unreachable', reason: null }
}

function stateFromStatus(status: QueryStatus, error: unknown): ConnectionState {
  if (status === 'success') {
    return 'connected'
  }
  if (status === 'error') {
    return stateFromError(error)
  }
  return 'checking'
}

function stateFromError(error: unknown): ConnectionState {
  if (!(error instanceof HttpErrorResponse)) {
    return 'unreachable'
  }
  if (error.status === 401) {
    return 'signInRequired'
  }
  if (error.status === 403) {
    return 'notPermitted'
  }
  // Status 0 covers both a dead host and a response the browser refused to expose, which is what an
  // environment that has not deployed the endpoint yet looks like from here.
  return 'unreachable'
}

@Injectable({ providedIn: 'root' })
export class EnvironmentFanOutService {
  readonly environments = inject(SUPPORT_ENVIRONMENTS)
  private httpClient = inject(HttpClient)
  private query = injectQuery()
  private queryClient = injectQueryClient()

  /**
   * One state per environment, merged across every fan-out the app is subscribed to right now.
   *
   * Taken from the query cache rather than from a probe of its own, so that it speaks for what the
   * reader is looking at: an environment can answer one endpoint and refuse another — a route the
   * load balancer turns away, or one it has not deployed yet — and a probe would call that connected
   * while the page in front of them is quietly missing its rows. Reading it also costs no request.
   */
  readonly connections$: Observable<EnvironmentResult<unknown>[]> = new Observable<void>((subscriber) => {
    subscriber.next()
    return this.queryClient.getQueryCache().subscribe(() => subscriber.next())
  }).pipe(
    map(() => this.readConnections()),
    distinctUntilChanged((before, after) => connectionStamp(before) === connectionStamp(after)),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  fanOut<T>(request: FanOutRequest): Observable<EnvironmentResult<T>[]> {
    const targets = this.environments.filter((environment) => request.appliesTo?.(environment) ?? true)
    // combineLatest over nothing completes without ever emitting, which would leave the screen on its
    // spinner rather than telling the reader there is nothing to show.
    return targets.length === 0 ? of([]) : combineLatest(targets.map((environment) => this.watch<T>(environment, request)))
  }

  /**
   * Posts once to every environment the request has a body for, and reports each one separately. No
   * environment waits on another and none of them roll back: a partial result is the normal outcome
   * and the caller has to be able to say which half worked.
   */
  createInEach<T, B>(request: WriteFanOutRequest<B>): Observable<EnvironmentWriteResult<T>[]> {
    return forkJoin(this.environments.map((environment) => this.createIn<T, B>(environment, request)))
  }

  refresh(request: FanOutRequest, environment?: SupportEnvironment) {
    const targets = environment === undefined ? this.environments : [environment]
    for (const target of targets) {
      void this.queryClient.refetchQueries({ queryKey: this.queryKeyFor(target, request) })
    }
  }

  private createIn<T, B>(environment: SupportEnvironment, request: WriteFanOutRequest<B>): Observable<EnvironmentWriteResult<T>> {
    const body = request.body(environment)
    if (body === null) {
      return of({ environment, state: 'skipped' as const, data: null, reason: null })
    }
    return this.httpClient.post<T>(`${environment.origin}${request.path}`, body, { withCredentials: !isSelfEnvironment(environment) }).pipe(
      map((data) => ({ environment, state: 'created' as const, data, reason: null })),
      catchError((error: unknown) => of({ environment, data: null, ...writeStateFromError(error) }))
    )
  }

  // Only the queries something is subscribed to: one left behind by the page before this one is
  // reporting on rows nobody is looking for any more.
  private readConnections(): EnvironmentResult<unknown>[] {
    const live = this.queryClient
      .getQueryCache()
      .getAll()
      .filter((query) => query.isActive())
      .map((query) => this.connectionFrom(query))
      .filter((result): result is EnvironmentResult<unknown> => result !== null)
    return worstStatePerEnvironment(this.environments, live)
  }

  // A fan-out is the only thing keyed by environment; everything else in the cache belongs to a
  // screen that reads one backend and already has an error state of its own.
  private connectionFrom(query: Query): EnvironmentResult<unknown> | null {
    const [scope, key] = query.queryKey
    const environment = this.environments.find((candidate) => candidate.key === key)
    if (scope !== 'environment' || environment === undefined) {
      return null
    }
    const checkedAt = Math.max(query.state.dataUpdatedAt, query.state.errorUpdatedAt)
    return {
      environment,
      state: stateFromStatus(query.state.status, query.state.error),
      data: null,
      checkedAt: checkedAt === 0 ? null : new Date(checkedAt),
    }
  }

  private queryKeyFor(environment: SupportEnvironment, request: FanOutRequest): unknown[] {
    return ['environment', environment.key, ...request.queryKey]
  }

  private watch<T>(environment: SupportEnvironment, request: FanOutRequest): Observable<EnvironmentResult<T>> {
    const isSelf = isSelfEnvironment(environment)
    return this.query({
      queryKey: this.queryKeyFor(environment, request),
      queryFn: () =>
        this.httpClient.get<T>(`${environment.origin}${request.path}`, {
          params: typeof request.params === 'function' ? request.params(environment) : request.params,
          withCredentials: !isSelf,
        }),
      // A remote environment answering 401 or 403 is a state the roll-call renders a button for, not
      // a fault worth retrying. The serving environment keeps the default policy so that the
      // single-environment case behaves exactly as it did before the fan-out existed.
      ...(isSelf ? {} : { retry: 0 }),
      refetchInterval: request.refetchInterval,
    }).result$.pipe(
      map((result) => {
        const checkedAt = Math.max(result.dataUpdatedAt, result.errorUpdatedAt)
        return {
          environment,
          state: stateFromStatus(result.status, result.error),
          data: result.data ?? null,
          checkedAt: checkedAt === 0 ? null : new Date(checkedAt),
        }
      })
    )
  }
}

function connectionStamp(results: EnvironmentResult<unknown>[]): string {
  return results.map((result) => `${result.environment.key}:${result.state}`).join('|')
}
